import { buildingRoomDisplay, cleanVal } from '../../../../lib/formatUtils';
import {
  getGroupDisplayLabel,
  isBoardGroupedSession,
  snakeToTitleCase,
} from '../../../../lib/sessionGroupingUtils';

/**
 * Derive the display fields for a mobile session group card from real asset
 * data — never placeholder values.
 *
 * Hides: representative-asset selection (the distribution board leads a DB+RCD
 * group, otherwise the first item), null/"NULL" cleaning, location dedupe/omit,
 * and tested-progress counting. Consumers get a flat, ready-to-render shape.
 *
 * @param {string} groupName - group key from groupAssetsByType
 * @param {Array<object>} items - assets in the group
 * @param {string} sessionAssetType - active session asset type
 * @param {Record<string|number, unknown>} [results] - session results keyed by asset id
 * @returns {{ groupCode: string, assetType: string, location: string, testedCount: number, totalCount: number }}
 */
export function deriveMobileGroupCardFields(groupName, items, sessionAssetType, results = {}) {
  const list = Array.isArray(items) ? items : [];
  const resultMap = results || {};

  const totalCount = list.length;
  const testedCount = list.reduce((count, asset) => (resultMap[asset.id] ? count + 1 : count), 0);

  const representative =
    list.find((asset) => (asset.asset_type || '').toLowerCase() === 'distribution_board') ||
    list[0] ||
    null;

  const groupCode =
    cleanVal(groupName) || getGroupDisplayLabel(groupName, list, sessionAssetType) || 'Group';

  const assetType = representative?.asset_type ? snakeToTitleCase(representative.asset_type) : '';

  // Only board-grouped sessions get a location line. Elsewhere the group IS a
  // building, so this would render the FIRST asset's room as though it described
  // the whole group — "B00A – R0003" above 27 lights spread across the building.
  // The building is already the tile title; room belongs on the asset cards inside.
  const locationRaw = isBoardGroupedSession(sessionAssetType) && representative
    ? buildingRoomDisplay(representative.building, representative.room)
    : '-';
  // Omit when empty or when it would just repeat the group code.
  const location = locationRaw && locationRaw !== '-' && locationRaw !== groupCode ? locationRaw : '';

  return { groupCode, assetType, location, testedCount, totalCount };
}
